// Libs
import * as admin from "firebase/firestore";
import { z } from "zod";

// Local
import { dropUndefined } from "../utils/dropUndefined";
import { guardIsPlainObject } from "../utils/isPlainObject";
import { NotFoundError } from "../error-classes";
import { ExistingSiteKeyLocation } from "./site-key-location";
import {
  AccountingSyncStiltDoc,
  AccountingSyncStiltDocSchema,
} from "./accounting-sync";
import { ExistingPurchaseOrder } from "./purchase-order";
import { zFallback } from "../utils/zod-fallback";

type DocumentData = admin.DocumentData;
type DocumentSnapshot = admin.DocumentSnapshot;
type TimestampType = admin.Timestamp;
const Timestamp = admin.Timestamp;

/**
 * PurchaseOrderReceipt model for tracking items received against purchase orders
 */
export interface PurchaseOrderReceipt {
  timestampCreated: TimestampType;
  createdBy: string;
  timestampLastModified: TimestampType;
  lastModifiedBy: string;
  locationID: string;
  purchaseOrderID: string;
  vendorID: string;
  vendorName: string;
  receivedDate: TimestampType;
  receivedBy: string;
  invoiceNumber: string | null;
  receivingNotes: string | null;
  internalNotes: string | null;
  deleted: boolean;
  totalQuantityReceived: number;
  lineItems: PurchaseOrderReceiptItem[];
  customData: { [key: string]: any };
  accountingSync?: AccountingSyncStiltDoc;
}

/**
 * PurchaseOrderReceiptItem model for tracking individual items received in a receipt
 */
export interface PurchaseOrderReceiptItem {
  purchaseOrderItemID: string; // Unique ID of the line item in the PO's lineItems array
  priceBookItemID: string | null;
  title: string;
  description: string | null;
  quantityOrdered: number;
  quantityReceived: number;
  quantityRejected: number;
  rejectionReason: string | null;
  unitPrice: number;
}

export interface PurchaseOrderReceiptItemWithLocation
  extends PurchaseOrderReceiptItem {
  location: ExistingSiteKeyLocation | null;
  receipt: ExistingPurchaseOrderReceipt;
}

export interface ExistingPurchaseOrderReceipt extends PurchaseOrderReceipt {
  id: string;
  refPath: string;
}

export interface PurchaseOrderReceiptWithPurchaseOrder
  extends ExistingPurchaseOrderReceipt {
  purchaseOrder: ExistingPurchaseOrder;
}

/***********************/
// FOR THE API
/***********************/
type withoutTimestamps = Omit<
  PurchaseOrderReceipt,
  "timestampCreated" | "timestampLastModified"
>;

type withoutAllTimestamps = Omit<
  PurchaseOrderReceipt,
  "timestampCreated" | "timestampLastModified" | "receivedDate"
>;

export interface ExistingPurchaseOrderReceiptISOTimestamps
  extends Omit<
    ExistingPurchaseOrderReceipt,
    "timestampCreated" | "timestampLastModified" | "receivedDate"
  > {
  timestampCreated: string;
  timestampLastModified: string;
  receivedDate: string;
}

// Timestamps are added on the backend.
export interface PurchaseOrderReceipt_CreateAPI extends withoutAllTimestamps {
  siteKey: string;
  // ISO string
  receivedDate: string;
}

export type PurchaseOrderReceipt_UpdateAPI = Omit<
  Partial<ExistingPurchaseOrderReceipt>,
  "timestampCreated" | "createdBy"
>;

// #endregion

// #region SECTION: Functions
/** Utilities for interacting with PurchaseOrderReceipt objects  */
export const PurchaseOrderReceiptManager = {
  /** Drop `id` and `refPath` before saving to the database. Drop undefined */
  convertForFirestore: convertPurchaseOrderReceiptForFirestore,
  createFromFirestoreSnapshot: createFromFirestoreSnapshot,
  convertTimestampsToISO: convertTimestampsToISO,
  /** Use when validating something outgoing - writing to the DB or reading from the user */
  parse: validatePurchaseOrderReceipt,
  parseCreate: validatePurchaseOrderReceipt_Create,
  parseUpdate: validatePurchaseOrderReceipt_Update,
  /** Validate a PurchaseOrderReceipt doc, with fallbacks. Use for reading from the database. */
  parseWithFallbacks: validatePurchaseOrderReceiptWithFallbacks,
  /** Convert receipt to a readable string format for LLM */
  convertForLLM,
} as const;

/** Drop `id` and `refPath` before saving to the database. Drop undefined values */
function convertPurchaseOrderReceiptForFirestore(
  receipt: ExistingPurchaseOrderReceipt | PurchaseOrderReceipt_UpdateAPI,
): DocumentData {
  const local = Object.assign({}, receipt);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, refPath, ...rest } = local;
  return dropUndefined(rest);
}

function createFromFirestoreSnapshot(
  snapshot: DocumentSnapshot,
): ExistingPurchaseOrderReceipt {
  if (!snapshot.exists) {
    throw new NotFoundError("Document does not exist.");
  }
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validatePurchaseOrderReceiptWithFallbacks(snapshot.data()),
  };
}

function convertTimestampsToISO(
  receipt: ExistingPurchaseOrderReceipt,
): ExistingPurchaseOrderReceiptISOTimestamps {
  const data: ExistingPurchaseOrderReceiptISOTimestamps = {
    id: receipt.id,
    refPath: receipt.refPath,
    createdBy: receipt.createdBy,
    lastModifiedBy: receipt.lastModifiedBy,
    locationID: receipt.locationID,
    purchaseOrderID: receipt.purchaseOrderID,
    invoiceNumber: receipt.invoiceNumber,
    vendorID: receipt.vendorID,
    vendorName: receipt.vendorName,
    receivedBy: receipt.receivedBy,
    receivingNotes: receipt.receivingNotes,
    internalNotes: receipt.internalNotes,
    deleted: receipt.deleted,
    totalQuantityReceived: receipt.totalQuantityReceived,
    lineItems: receipt.lineItems.map((item) => {
      const result = { ...item };
      return result;
    }),
    customData: receipt.customData,
    timestampCreated: receipt.timestampCreated.toDate().toISOString(),
    timestampLastModified: receipt.timestampLastModified.toDate().toISOString(),
    receivedDate: receipt.receivedDate.toDate().toISOString(),
  };

  if (receipt.accountingSync) {
    data.accountingSync = receipt.accountingSync;
  }

  return data;
}

function convertForLLM(receipt: ExistingPurchaseOrderReceipt): string {
  const {
    id,
    invoiceNumber,
    purchaseOrderID,
    vendorName,
    receivedDate,
    receivedBy,
    totalQuantityReceived,
    lineItems,
    deleted,
  } = receipt;

  const parts: string[] = [];

  // Add basic info
  parts.push(`This is a purchase order receipt with an internal ID of ${id}`);

  // Add receipt number and purchase order ID
  parts.push(
    `with invoice number ${invoiceNumber} for purchase order ${purchaseOrderID}`,
  );

  // Add vendor info
  parts.push(`from vendor "${vendorName}"`);

  // Add deleted status if applicable
  if (deleted) {
    parts.push(`(deleted)`);
  }

  // Add received info
  parts.push(
    `received on ${receivedDate.toDate().toISOString().split("T")[0]} by ${receivedBy}`,
  );

  // Add quantity and line items count
  parts.push(
    `with total of ${totalQuantityReceived} items received across ${lineItems.length} line items`,
  );

  return parts.join(" ");
}

/* Zod validation schemas */
function validatePurchaseOrderReceipt(value: unknown): PurchaseOrderReceipt {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = purchaseOrderReceiptSchema.safeParse(value);
  if (!result.success) {
    throw new Error(`Invalid purchase order receipt: ${result.error}`);
  }
  return result.data;
}

function validatePurchaseOrderReceiptWithFallbacks(
  value: unknown,
): PurchaseOrderReceipt {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = purchaseOrderReceiptSchemaWithFallbacks.parse(value);
  return result;
}

function validatePurchaseOrderReceipt_Create(
  value: unknown,
): PurchaseOrderReceipt_CreateAPI {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return purchaseOrderReceiptSchema_CreateAPI.parse(value);
}

function validatePurchaseOrderReceipt_Update(
  value: unknown,
): PurchaseOrderReceipt_UpdateAPI {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return purchaseOrderReceiptSchema_UpdateAPI.parse(value);
}

// #endregion

// #region SECTION: Schemas

export const PurchaseOrderReceiptItemSchema = z.object({
  purchaseOrderItemID: z.string().min(1).max(200),
  priceBookItemID: z.string().min(1).max(200).nullable(),
  title: z.string().min(1).max(2000),
  description: z.string().min(0).max(4000).nullable(),
  quantityOrdered: z.number().min(0),
  quantityReceived: z.number().min(0),
  quantityRejected: z.number().min(0),
  rejectionReason: z.string().min(0).max(4000).nullable(),
  unitPrice: z.number(),
});

const purchaseOrderReceiptSchema = z.object({
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  createdBy: z.string().min(1).max(200),
  lastModifiedBy: z.string().min(1).max(200),
  locationID: z.string().min(1).max(200),
  purchaseOrderID: z.string().min(1).max(200),
  invoiceNumber: z.string().min(1).max(200).nullable(),
  vendorID: z.string().min(1).max(200),
  vendorName: z.string().min(1).max(200),
  receivedDate: z.instanceof(Timestamp),
  receivedBy: z.string().min(0).max(200),
  receivingNotes: z.string().min(0).max(8000).nullable(),
  internalNotes: z.string().min(0).max(8000).nullable(),
  deleted: z.boolean().default(false),
  totalQuantityReceived: z.number().min(0),
  lineItems: z.array(PurchaseOrderReceiptItemSchema).min(0),
  customData: z.record(z.any()),
  accountingSync: AccountingSyncStiltDocSchema.optional(),
});

const purchaseOrderReceiptSchemaWithFallbacks = z.object({
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  createdBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderReceiptSchemaWithFallbacks: 'createdBy'",
  ),
  lastModifiedBy: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderReceiptSchemaWithFallbacks: 'lastModifiedBy'",
  ),
  locationID: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderReceiptSchemaWithFallbacks: 'locationID'",
  ),
  purchaseOrderID: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderReceiptSchemaWithFallbacks: 'purchaseOrderID'",
  ),
  invoiceNumber: zFallback(
    z.string().min(1).max(200).nullable(),
    null,
    "purchaseOrderReceiptSchemaWithFallbacks: 'invoiceNumber'",
  ),
  vendorID: zFallback(
    z.string().min(1).max(200),
    "unknown",
    "purchaseOrderReceiptSchemaWithFallbacks: 'vendorID'",
  ),
  vendorName: zFallback(
    z.string().min(1).max(200),
    "Unknown Vendor",
    "purchaseOrderReceiptSchemaWithFallbacks: 'vendorName'",
  ),
  receivedDate: z.instanceof(Timestamp),
  receivedBy: zFallback(
    z.string().min(0).max(200),
    "",
    "purchaseOrderReceiptSchemaWithFallbacks: 'receivedBy'",
  ),
  receivingNotes: zFallback(
    z.string().min(0).max(8000).nullable(),
    null,
    "purchaseOrderReceiptSchemaWithFallbacks: 'receivingNotes'",
  ),
  internalNotes: zFallback(
    z.string().min(0).max(8000).nullable(),
    null,
    "purchaseOrderReceiptSchemaWithFallbacks: 'internalNotes'",
  ),
  deleted: zFallback(
    z.boolean().default(false),
    false,
    "purchaseOrderReceiptSchemaWithFallbacks: 'deleted'",
  ),
  totalQuantityReceived: zFallback(
    z.number().min(0),
    0,
    "purchaseOrderReceiptSchemaWithFallbacks: 'totalQuantityReceived'",
  ),
  lineItems: zFallback(
    z.array(PurchaseOrderReceiptItemSchema).min(0),
    [],
    "purchaseOrderReceiptSchemaWithFallbacks: 'lineItems'",
  ),
  customData: zFallback(
    z.record(z.any()),
    {},
    "purchaseOrderReceiptSchemaWithFallbacks: 'customData'",
  ),
  accountingSync: AccountingSyncStiltDocSchema.optional(),
});

const withoutTimestampsSchema = purchaseOrderReceiptSchema.omit({
  timestampCreated: true,
  timestampLastModified: true,
});

const withoutAllTimestampsSchema = purchaseOrderReceiptSchema.omit({
  timestampCreated: true,
  timestampLastModified: true,
  receivedDate: true,
});

// Used for interacting with the Create endpoint.
const purchaseOrderReceiptSchema_CreateAPI = withoutAllTimestampsSchema.extend({
  siteKey: z.string().min(1).max(400),
  // ISO string
  receivedDate: z.string().min(1).max(400),
});

// Used for interacting with the Update endpoint
const purchaseOrderReceiptSchema_UpdateAPI = withoutTimestampsSchema
  .extend({
    id: z.string().min(1).max(200),
    refPath: z.string().min(1).max(400),
  })
  .partial();

// #endregion
